import { useRef, useState, type DragEvent } from 'react';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { storeImageFile, MediaValidationError, ACCEPTED_IMAGE_TYPES } from '@/lib/media';
import { SectionCard } from '@/components/SectionCard';
import { Button } from '@/components/Button';
import { Select } from '@/components/Select';
import { Slider } from '@/components/Slider';
import { Toggle } from '@/components/Toggle';
import { ColorPicker } from '@/components/ColorPicker';
import { ImageThumb } from './ImageThumb';
import type { FitMode, ReelProject, TransitionType } from '@/types';

interface ImagesPanelProps {
  project: ReelProject;
}

const FIT_OPTIONS: { value: FitMode; label: string }[] = [
  { value: 'cover', label: 'Cover (fill & crop)' },
  { value: 'contain', label: 'Contain (fit, no crop)' },
  { value: 'fill', label: 'Fill (stretch)' },
];

const TRANSITION_OPTIONS: { value: TransitionType; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'fade', label: 'Fade' },
  { value: 'crossfade', label: 'Crossfade' },
  { value: 'slide', label: 'Slide' },
  { value: 'zoom', label: 'Zoom' },
];

export function ImagesPanel({ project }: ImagesPanelProps) {
  const addImages = useWorkspaceStore((s) => s.addImages);
  const reorderImages = useWorkspaceStore((s) => s.reorderImages);
  const removeImage = useWorkspaceStore((s) => s.removeImage);
  const replaceImage = useWorkspaceStore((s) => s.replaceImage);
  const updateImageClip = useWorkspaceStore((s) => s.updateImageClip);
  const applyDurationToAll = useWorkspaceStore((s) => s.applyDurationToAll);

  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uniformDuration, setUniformDuration] = useState(3);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const replaceTargetId = useRef<string | null>(null);
  const dragIndex = useRef<number | null>(null);

  const sortedImages = [...project.images].sort((a, b) => a.order - b.order);
  const selectedImage = sortedImages.find((i) => i.id === selectedImageId) ?? null;

  async function handleFiles(files: FileList | File[]) {
    setUploadError(null);
    const assets = [];
    for (const file of Array.from(files)) {
      try {
        assets.push(await storeImageFile(file));
      } catch (err) {
        setUploadError(err instanceof MediaValidationError ? err.message : 'Could not process one of the selected images.');
      }
    }
    if (assets.length > 0) addImages(project.id, assets, uniformDuration);
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files.length > 0) void handleFiles(e.dataTransfer.files);
  }

  function onListDrop(targetIndex: number) {
    if (dragIndex.current === null) return;
    const ids = sortedImages.map((i) => i.id);
    const [moved] = ids.splice(dragIndex.current, 1);
    ids.splice(targetIndex, 0, moved);
    reorderImages(project.id, ids);
    dragIndex.current = null;
  }

  return (
    <div className="space-y-4">
      <SectionCard title="Upload images">
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
            isDragOver ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20' : 'border-slate-300 dark:border-slate-600'
          }`}
        >
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Drag & drop images here, or click to browse</p>
          <p className="mt-1 text-xs text-slate-400">JPG, PNG or WebP</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_IMAGE_TYPES.join(',')}
            className="hidden"
            onChange={(e) => e.target.files && handleFiles(e.target.files)}
          />
        </div>
        {uploadError && <p className="mt-2 text-xs text-red-600">{uploadError}</p>}

        <div className="mt-3 flex items-end gap-2">
          <div className="flex-1">
            <Slider
              label="Uniform image duration"
              value={uniformDuration}
              min={0.5}
              max={15}
              step={0.5}
              formatValue={(v) => `${v}s`}
              onChange={setUniformDuration}
              tooltip="Applied to newly uploaded images, or click 'Apply to all' to update every existing image."
            />
          </div>
          <Button size="sm" onClick={() => applyDurationToAll(project.id, uniformDuration)} disabled={sortedImages.length === 0}>
            Apply to all
          </Button>
        </div>
      </SectionCard>

      <SectionCard title={`Image sequence (${sortedImages.length})`}>
        {sortedImages.length === 0 ? (
          <p className="text-xs text-slate-400">No images yet — upload some above to build your Reel.</p>
        ) : (
          <ul className="space-y-2">
            {sortedImages.map((img, index) => (
              <li
                key={img.id}
                draggable
                onDragStart={() => (dragIndex.current = index)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onListDrop(index)}
                onClick={() => setSelectedImageId(img.id)}
                className={`flex cursor-grab items-center gap-3 rounded-lg border p-2 transition-colors active:cursor-grabbing ${
                  selectedImageId === img.id
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
                    : 'border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800'
                }`}
              >
                <span className="text-slate-400">⠿</span>
                <ImageThumb mediaId={img.mediaId} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium text-slate-700 dark:text-slate-200">Image {index + 1}</p>
                  <p className="text-xs text-slate-400">
                    {img.duration}s · {img.fit} · {img.transitionIn}
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    replaceTargetId.current = img.id;
                    replaceInputRef.current?.click();
                  }}
                  className="text-xs text-slate-500 hover:text-brand-600"
                  title="Replace image"
                >
                  Replace
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeImage(project.id, img.id);
                    if (selectedImageId === img.id) setSelectedImageId(null);
                  }}
                  className="text-xs text-red-500 hover:text-red-700"
                  title="Remove image"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
        <input
          ref={replaceInputRef}
          type="file"
          accept={ACCEPTED_IMAGE_TYPES.join(',')}
          className="hidden"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (file && replaceTargetId.current) {
              try {
                const asset = await storeImageFile(file);
                replaceImage(project.id, replaceTargetId.current, asset.id);
              } catch (err) {
                setUploadError(err instanceof MediaValidationError ? err.message : 'Could not replace image.');
              }
            }
            e.target.value = '';
          }}
        />
      </SectionCard>

      {selectedImage && (
        <SectionCard title={`Image settings`}>
          <Slider
            label="Duration on screen"
            value={selectedImage.duration}
            min={0.5}
            max={15}
            step={0.5}
            formatValue={(v) => `${v}s`}
            onChange={(v) => updateImageClip(project.id, selectedImage.id, { duration: v })}
          />
          <Select
            label="Fit mode"
            value={selectedImage.fit}
            options={FIT_OPTIONS}
            onChange={(v) => updateImageClip(project.id, selectedImage.id, { fit: v as FitMode })}
          />
          {selectedImage.fit === 'contain' && (
            <ColorPicker
              label="Background colour"
              value={selectedImage.backgroundColor}
              onChange={(v) => updateImageClip(project.id, selectedImage.id, { backgroundColor: v })}
            />
          )}
          <Select
            label="Transition in"
            value={selectedImage.transitionIn}
            options={TRANSITION_OPTIONS}
            onChange={(v) => updateImageClip(project.id, selectedImage.id, { transitionIn: v as TransitionType })}
          />
          {selectedImage.transitionIn !== 'none' && (
            <Slider
              label="Transition duration"
              value={selectedImage.transitionDuration}
              min={0.1}
              max={2}
              step={0.1}
              formatValue={(v) => `${v.toFixed(1)}s`}
              onChange={(v) => updateImageClip(project.id, selectedImage.id, { transitionDuration: v })}
            />
          )}
          <Toggle
            label="Subtle pan & zoom (Ken Burns)"
            checked={selectedImage.kenBurns.enabled}
            onChange={(v) => updateImageClip(project.id, selectedImage.id, { kenBurns: { ...selectedImage.kenBurns, enabled: v } })}
            tooltip="Slowly zooms and pans the image while it's on screen."
          />
          {selectedImage.kenBurns.enabled && (
            <Slider
              label="Zoom amount"
              value={selectedImage.kenBurns.endScale}
              min={1}
              max={1.5}
              step={0.01}
              formatValue={(v) => `${Math.round((v - 1) * 100)}%`}
              onChange={(v) => updateImageClip(project.id, selectedImage.id, { kenBurns: { ...selectedImage.kenBurns, endScale: v } })}
            />
          )}
        </SectionCard>
      )}
    </div>
  );
}
